import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { ChartComponent } from '../../../layout/chart/chart.component';
import { FilterListContainerComponent } from '../../../layout/filter-list-container/filter-list-container.component';
import { DownloadButtonComponent } from '../../download-button/download-button.component';
import { CHART_TYPE, ChartTypes, CommonChartModel } from '../../../model/chart.model';
import { DataTableSchema, DefaultTableSchema, exportConfig, TableColumnSchema, TITLE_TYPE } from '../../../model/dynamic-data-table.model';
import { DynamicFormArray, FieldSelectedValue, FIELD_TYPES, InfoFieldConfig } from '../../../model/form.model';
import { SearchConfig } from '../../../model/api.model';
import { ExcelExportService } from '../../../services/excel-export.service';
import { CommonUtils } from '../../../utils/common.utils';

export const SURVEY_DRILLDOWN_COLUMNS: TableColumnSchema[] = [
  { label: 'Spin ID', fieldKey: 'spinId', sticky: true, isSortable: true },
  { label: 'Name', fieldKey: 'name', isSortable: true },
  { label: 'Answer', fieldKey: 'answer', isSortable: true, isNotCamelCase: true },
];

export const SURVEY_DRILLDOWN_EXPORT_CONFIG: exportConfig = {
  fileName: 'Survey_Question_Responses',
  exportMessage: 'Survey Question Responses',
  schema: { spinId: 'Spin ID', name: 'Name', answer: 'Answer' },
};

const RESPONSE_DETAILS_EXPORT_CONFIG: exportConfig = {
  fileName: 'Response_Details',
  exportMessage: 'Response Details',
  schema: { spinId: 'Spin ID', name: 'Name', answer: 'Answer' },
};

const ALL_RESPONSE_DETAILS_EXPORT_CONFIG: exportConfig = {
  fileName: 'Response_Details',
  exportMessage: 'Response Details',
  schema: { question: 'Question', spinId: 'Spin ID', name: 'Name', answer: 'Answer' },
};

interface SurveyResponseRow { spinId: string; name: string; answer: string; }

@Component({
  selector: 'app-survey-chart-drilldown',
  standalone: true,
  imports: [CommonModule, ChartComponent, FilterListContainerComponent, DownloadButtonComponent],
  templateUrl: './survey-chart-drilldown.component.html',
  styleUrl: './survey-chart-drilldown.component.scss'
})
export class SurveyChartDrilldownComponent implements OnChanges {
  @Input() chartSchema!: CommonChartModel;
  @Input() chartType!: ChartTypes;
  @Input() infoFields: InfoFieldConfig[] = [];
  @Input() elementId = '';
  @Input() containerClass?: string;
  @Input() apiConfig!: SearchConfig;
  @Input() exportConfig: exportConfig | null = null;
  @Input() dataTransform?: (response: any) => any[];
  @Input() dataInput?: any;
  @Input() onLoadFetch = true;
  @Input() drilldownDetailsKey = 'responseDetails';
  @Input() drilldownLabelKey = '';
  @Input() drilldownColumns: TableColumnSchema[] = SURVEY_DRILLDOWN_COLUMNS;
  @Input() drilldownExportConfig: exportConfig = SURVEY_DRILLDOWN_EXPORT_CONFIG;
  @Input() drilldownDisplay: 'table' | 'chart' = 'table';
  @Input() enableQuestionDropdown = false;
  @Input() answerBreakdownExportConfig: exportConfig = RESPONSE_DETAILS_EXPORT_CONFIG;

  private readonly excelService = inject(ExcelExportService);
  private readonly commonUtils = inject(CommonUtils);

  internalChartSchema!: CommonChartModel;

  showDrilldown = false;
  drilldownTitle = '';
  drilldownSchema!: DataTableSchema;
  readonly drilldownFilterSchema: DynamicFormArray = { FieldsArray: [], ButtonsArray: [], title: '' };

  chartLevel: 0 | 1 = 0;
  selectedQuestion = '';
  questionFilterSchema: DynamicFormArray = { FieldsArray: [], onControlValueChanged: (event: FieldSelectedValue) => this.onQuestionSelected(event.Value) };
  level1Schema!: CommonChartModel;
  level1Data: Array<{ answer: string; count: number }> = [];
  private selectedQuestionRows: SurveyResponseRow[] = [];
  chartDataset: any[] = [];

  get chartTitle(): string {
    const titleSchema: any = this.chartSchema?.titleSchema;
    if (!titleSchema) { return ''; }
    if (typeof titleSchema.title === 'string') { return titleSchema.title; }
    return Object.values(titleSchema).map((entry: any) => entry?.title).find((entry: any) => !!entry) ?? '';
  }

  get currentLevelExportRows(): any[] {
    return this.chartLevel === 0 ? this.chartDataset : this.level1Data;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chartSchema'] && this.chartSchema) {
      this.buildInternalChartSchema();
    }
  }

  internalDataTransform = (response: any): any[] => {
    const transformed = this.dataTransform ? this.dataTransform(response) : (response ?? []);
    this.chartDataset = Array.isArray(transformed) ? transformed : [];
    this.buildQuestionOptions();
    return transformed;
  };

  goBack(): void {
    this.showDrilldown = false;
  }

  goToLevel0(): void {
    this.chartLevel = 0;
  }

  onQuestionSelected(value: string): void {
    const matchedRow = this.chartDataset.find(row => String(row?.option ?? row?.question ?? '') === value);
    const rows = matchedRow?.[this.drilldownDetailsKey];
    if (!Array.isArray(rows) || rows.length === 0) { return; }
    this.openQuestionAnswerChart(rows, value);
  }

  exportCurrentLevel(): void {
    if (this.chartLevel === 0) {
      this.exportLevel0();
    } else {
      this.exportLevel1();
    }
  }

  private exportLevel0(): void {
    if (!this.chartDataset.length) { return; }
    const dataSheet = structuredClone(this.commonUtils.formatExcelData(this.exportConfig ?? { fileName: 'data' }, this.chartDataset)) ?? [];
    const allResponses = this.chartDataset.flatMap((item: any) => {
      const rows = Array.isArray(item?.[this.drilldownDetailsKey]) ? item[this.drilldownDetailsKey] : [];
      const question = String(item?.option ?? item?.question ?? '');
      return rows
        .filter((row: any) => String(row?.answer ?? '').trim() !== '')
        .map((row: any) => ({
          question,
          spinId: String(row?.spinId ?? row?.spin_id ?? ''),
          name: String(row?.name ?? ''),
          answer: String(row.answer),
        }));
    });
    const responseSheet = structuredClone(this.commonUtils.formatExcelData(ALL_RESPONSE_DETAILS_EXPORT_CONFIG, allResponses)) ?? [];
    const fileName = this.exportConfig?.fileName ?? 'Survey_Export';
    this.excelService.exportToMultipleSheets({ data: dataSheet, 'Response Details': responseSheet }, fileName);
  }

  private exportLevel1(): void {
    if (!this.level1Data.length) { return; }
    const countsSheet = structuredClone(this.commonUtils.formatExcelData(this.answerBreakdownExportConfig, this.level1Data)) ?? [];
    const detailsSheet = structuredClone(this.commonUtils.formatExcelData(RESPONSE_DETAILS_EXPORT_CONFIG, this.selectedQuestionRows)) ?? [];
    const fileName = this.answerBreakdownExportConfig?.fileName ?? 'Answer_Breakdown';
    this.excelService.exportToMultipleSheets({ data: countsSheet, 'Response Details': detailsSheet }, fileName);
  }

  private buildQuestionOptions(): void {
    if (!this.enableQuestionDropdown) { return; }
    const labels = this.chartDataset
      .map(row => String(row?.option ?? row?.question ?? ''))
      .filter(label => !!label);
    this.questionFilterSchema = {
      ...this.questionFilterSchema,
      FieldsArray: [{
        fieldName: 'question',
        displayName: 'Select Question',
        fieldType: FIELD_TYPES.DROPDOWN,
        defaultValue: '',
        disabled: false,
        options: labels.map(label => ({ fieldLabel: label, fieldValue: label })),
      }],
    };
  }

  private openQuestionAnswerChart(rows: any[], label: string): void {
    this.selectedQuestion = label;
    this.selectedQuestionRows = rows
      .filter((row: any) => String(row?.answer ?? '').trim() !== '')
      .map((row: any) => ({
        spinId: String(row?.spinId ?? row?.spin_id ?? ''),
        name: String(row?.name ?? ''),
        answer: String(row.answer),
      }));
    const counts = new Map<string, number>();
    this.selectedQuestionRows.forEach(row => {
      counts.set(row.answer, (counts.get(row.answer) ?? 0) + 1);
    });
    this.level1Data = Array.from(counts.entries())
      .map(([answer, count]) => ({ answer, count }))
      .sort((a, b) => b.count - a.count);
    this.level1Schema = this.buildLevel1Schema(label);
    this.chartLevel = 1;
  }

  private buildInternalChartSchema(): void {
    const cloned = structuredClone(this.chartSchema);
    const point = { events: { click: (event: any) => this.onPointClick(event) } };
    cloned.plotOptions = {
      ...cloned.plotOptions,
      series: { ...cloned.plotOptions?.series, cursor: 'pointer', point },
      pie: { ...cloned.plotOptions?.pie, cursor: 'pointer', point },
    };
    this.internalChartSchema = cloned;
  }

  private onPointClick(event: any): void {
    const clickedPoint = event?.point ?? event?.target;
    const sourceRow = clickedPoint?.dataset ?? {};
    const rows = sourceRow[this.drilldownDetailsKey];
    if (!Array.isArray(rows) || rows.length === 0) { return; }
    const labelFromRow = this.drilldownLabelKey ? sourceRow[this.drilldownLabelKey] : '';
    const label = String(labelFromRow || clickedPoint?.name || sourceRow['question'] || '');
    if (this.drilldownDisplay === 'chart') {
      this.openQuestionAnswerChart(rows, label);
    } else {
      this.drilldownTitle = label;
      this.drilldownSchema = this.buildDrilldownSchema(rows);
      this.showDrilldown = true;
    }
  }

  private buildLevel1Schema(label: string): CommonChartModel {
    return {
      type: CHART_TYPE.ColumnChart,
      titleSchema: { label: '', title: label, type: TITLE_TYPE.Static },
      dataSchema: { label: 'answer', value: 'count' },
      xAxis: { type: 'category', title: { text: 'Answer' } },
      yAxis: { title: { text: 'Count' } },
      series: [{ name: 'Count', color: '#238b91', data: [] }],
      exportConfig: { fileName: this.answerBreakdownExportConfig?.fileName ?? 'Answer_Breakdown' },
      tooltip: {
        headerFormat: `<span style="font-size:11px">{point.name}</span><br>`,
        pointFormat: `<span>Count</span>: <b>{point.y}</b><br/>`
      }
    };
  }

  private buildDrilldownSchema(rows: any[]): DataTableSchema {
    return {
      ...structuredClone(DefaultTableSchema),
      titleSchema: undefined,
      columnSchema: structuredClone(this.drilldownColumns),
      dataSource: structuredClone(rows),
      tableTitle: this.drilldownTitle,
      isPaginationRequired: true,
      isSearchRequired: true,
      isSortRequired: true,
      isExportRequired: false,
      isFilterListExportRequired: true,
      exportConfig: structuredClone(this.drilldownExportConfig),
    };
  }
}
